import { type SanityBlogPostPage } from '@data/sanity/queries/types/blog'
import { type SanityBlogPostBodyModule } from '@data/sanity/queries/types/modules'
import { getSanityImageUrl } from '@lib/image'
import { type Locale } from '@lib/language'
import { getPortableTextHtml } from './portable-text'
import { getPageUrl } from '@lib/routes'
import { type BlogPostSchema, type ImageSchema } from './types'

export const getBlogPostSchema = (
  locale: Locale,
  blogPost: SanityBlogPostPage,
  siteUrl: string
) => {
  const {
    _createdAt,
    _updatedAt,
    title,
    excerpt,
    slug,
    author,
    date,
    categories,
    coverImage,
    modules,
    seo,
  } = blogPost
  const domain = siteUrl.replace(/\/+$/g, '')
  const blogPostUrl = getPageUrl({
    locale,
    pageType: 'blogPost',
    slugs: slug.current,
  })

  const blogPostBodyModule = modules?.find(
    (module) => module._type == 'blogPostBody'
  ) as SanityBlogPostBodyModule | undefined
  const articleBody = getPortableTextHtml(blogPostBodyModule?.content)

  const imageUrl = getSanityImageUrl(coverImage, {
    width: coverImage?.asset?.metadata?.dimensions?.width,
    height: coverImage?.asset?.metadata?.dimensions?.height,
    fitMax: true,
    autoFormat: true,
  })
  const thumbnailUrl = getSanityImageUrl(seo?.shareGraphic, {
    // Width and height taken from `HeadSeo` component
    width: 1200,
    height: 630,
  })

  const schema: BlogPostSchema = {
    '@type': 'BlogPosting',
    headline: title,
    abstract: excerpt,
    articleBody,
    url: `${domain}${blogPostUrl}`,
    datePublished: new Date(date ?? _createdAt),
    dateModified: new Date(_updatedAt),
    keywords: categories?.map((category) => category.name),
    thumbnailUrl,
  }

  if (author) {
    const blogAuthorUrl = getPageUrl({
      locale,
      pageType: 'blogAuthor',
      slugs: author.slug.current,
    })
    schema.author = {
      '@type': 'Person',
      name: author.name,
      url: `${domain}${blogAuthorUrl}`,
    }
  }

  if (imageUrl) {
    const imageWidth = coverImage?.asset?.metadata?.dimensions?.width
    const imageHeight = coverImage?.asset?.metadata?.dimensions?.height
    const image: ImageSchema = {
      '@type': 'ImageObject',
      '@id': imageUrl,
      url: imageUrl,
    }

    if (imageWidth) {
      image.width = `${imageWidth}`
    }
    if (imageHeight) {
      image.height = `${imageHeight}`
    }

    schema.image = image
  }

  return JSON.stringify(schema)
}
